define([
	"underscore",
	"csi/data/ExtendedDataPoint",
	"csi/core/ExtendedPath",
	"csi/core/Key",
	"csi/core/SpecBuilder",
	"csi/xml"
], function(_, ExtendedDataPoint, ExtendedPath, Key, SpecBuilder, xml) {

	function getFilterOperation(comparisonOperation) {
		var op = xml.enumGetText(comparisonOperation);
		switch (op) {
			case "Equals": return xml.OP_EQUALS;
			case "NotEquals": return xml.OP_NOT_EQUALS;
			case "GreaterThan": return xml.OP_GREATER_THAN;
			case "GreaterThanOrEquals": return xml.OP_GREATER_EQUAL;
			case "LessThan": return xml.OP_LESS_THAN;
			case "LessThanOrEqual": return xml.OP_LESS_EQUAL;
			case "Matches": return xml.OP_MATCHES;
			case "Contains": return xml.OP_EQUALS;
		}
		throw new Error("Unsupported comparisonOperation=" + comparisonOperation);
	}

	function convertDataPointToValue(/*Node*/nodeRoot, /*ExtendedDataPoint*/dp, /*Cache*/cache) {
		var node = cache.getPathNode(nodeRoot, dp.getExtendedPath().getStandardPath(), true);
		if (!node) {
			return null;
		}

		return xml.isNodeProperty(dp.getId()) ? xml.nodeGetProperty(node, dp.getId()) : node[dp.getId()];
	}

	function _addDay(/*Any*/value) {
		// summary: add almost one day to time range for 'less/greater than' to work -
		// only do so though if date set to midnight
		var number = Number(value);
		return number + ((new Date(number).getHours() == 0) ? (86400000 - 1) : 0);
	}

	/**
	 * Return path to use in query filter for given attribute
	 * @param {ExtendedDataPoint} dpAttribute 
	 * @param {ExtendedPath} [prefixPath]
	 * @return {string|null}
	 */
	function getFullPath(dpAttribute, prefixPath) {
		const extendedPath = dpAttribute.getExtendedPath();

		if (extendedPath && prefixPath) {
			const ep = prefixPath.clone();
			ep.addPath(extendedPath);
			return ep;
		}

		return extendedPath || prefixPath;
	}

	function _addFilter(/*Query*/qn, /*ExtendedPath*/prefixPath, /*String*/op, /*ExtendedDataPoint*/dpAttribute, /*Any*/value) {
		// summary: add filter to query node
		var id = dpAttribute.getId();
		const ep = getFullPath(dpAttribute, prefixPath);
		const queryPath = ep ? ep.getQueryPath() : null;

		if (xml.isNodeProperty(id)) {
			qn.Node(id, op, value, queryPath);
		} else {
			var attrType = dpAttribute.getDataType();
			var params = {
				Path: queryPath
			};

			if (xml.attributeTypeIsNumeric(attrType) || (attrType == xml.ATTR_TIME)) {
				params.DValue = value;
			} else {
				params.SValue = value;
			}

			qn.Attr2(id, op, params);
		}
	}

	function _applyFilter(/*Query*/qn, /*ExtendedPath*/prefixPath, /*String*/op, /*ExtendedDataPoint*/dp, /*Any*/value) {
		// summary: apply filter based on the type of data point
		var attrType = dp.getDataType();

		if (attrType == xml.ATTR_TIME) {
			// adjust hour of day for various time comparison operations
			if (op == xml.OP_EQUALS) {
				_addFilter(qn, prefixPath, xml.OP_GREATER_EQUAL, dp, value);
				_addFilter(qn, prefixPath, xml.OP_LESS_EQUAL, dp, _addDay(value));
			} else if (op == xml.OP_NOT_EQUALS) {
				var qor = qn.Or();
				_addFilter(qor, prefixPath, xml.OP_LESS_THAN, dp, value);
				_addFilter(qor, prefixPath, xml.OP_GREATER_THAN, dp, _addDay(value));
			} else if ((op == xml.OP_LESS_EQUAL) || (op == xml.OP_GREATER_THAN)) {
				_addFilter(qn, prefixPath, op, dp, _addDay(value));
			} else {
				_addFilter(qn, prefixPath, op, dp, value);
			}
		} else {
			const extendedPath = getFullPath(dp, prefixPath);
			const key = new Key({
				id: dp.getId(),
				extendedPath,
			});
			const spec = new SpecBuilder().getSpecForKey(key);
			spec.applyFilter(qn, op, value);
		}
	}

	function create(/*Query*/query, /*CNL*/urlQueryFilter, /*CNL*/urlRootNode, /*Cache*/cache) {
		createWithPrefixPath(query, null, urlQueryFilter, urlRootNode, cache);
	}

	function createWithPrefixPath(/*Query*/query, /*ExtendedPath*/prefixPath, /*CNL*/urlQueryFilter, /*CNL*/urlRootNode, /*Cache*/cache) {
		var nodeQueryFilter = cache.getNode(urlQueryFilter, true);
		if (!nodeQueryFilter)
			return;

		if ("QueryFilter_BooleanExpression" == nodeQueryFilter.$Type) {
			// create a boolean operation
			var queryBooleanOp = query;
			switch (nodeQueryFilter.Operation) {
				case "QueryFilter_BooleanOperation:AND":
					queryBooleanOp = query.And();
					break;
				case "QueryFilter_BooleanOperation:OR":
					queryBooleanOp = query.Or();
					break;
				case "QueryFilter_BooleanOperation:NOT":
					queryBooleanOp = query.Not();
					break;
			}

			// for each operand, create a filter inside of boolean operation
			_.each(nodeQueryFilter.Operands, (urlOperand) => {
				createWithPrefixPath(queryBooleanOp, prefixPath, urlOperand, urlRootNode, cache);
			});
		} else {
			var nodeDataPointContainer = cache.getNode(nodeQueryFilter.DataPointContainer, true);
			var dpAttribute = ExtendedDataPoint.parse(nodeDataPointContainer.DataPointMap.attribute);
			var dpValue1 = ExtendedDataPoint.parse(nodeDataPointContainer.DataPointMap.value);
			var value1 = nodeQueryFilter.Value;

			if (dpValue1) {
				value1 = convertDataPointToValue(cache.getNode(urlRootNode, true), dpValue1, cache);
				if (value1 == null)
					console.warn("The value is null or cannot be calculated for the data point='" + dpValue1.toString() + "' for the node='" + urlRootNode + "'");
			} else {
				if (value1 == null)
					console.warn("The 'Value' attribute is null in the query filter='" + urlQueryFilter + "'");
			}

			if (value1 != null) {
				switch (nodeQueryFilter.Operation) {
					case "QueryFilter_ComparisonOperation:NotMatches":
						_applyFilter(query.Not(), prefixPath, xml.OP_MATCHES, dpAttribute, value1);
						break;
					case "QueryFilter_ComparisonOperation:NotContains":
						_applyFilter(query.Not(), prefixPath, xml.OP_EQUALS, dpAttribute, value1);
						break;
					default:
						_applyFilter(query, prefixPath, getFilterOperation(nodeQueryFilter.Operation), dpAttribute, value1);
						break;
				}
			}
		}
	}

	return {
		create,
		createWithPrefixPath,
	};
});